import { useState, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { Helmet } from 'react-helmet';

import StyledWrapper from './style';

import {
	Alert,
	Collapse,
	IconButton,
	Box,
	Stepper,
	Step,
	StepLabel,
	Button,
	Typography,
	CircularProgress
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';

import FormGenerator from '../FormBuilderLibrary/FormGenerator';
import AppLocale from '../FormBuilderLibrary/language-provider';
import { IntlProvider } from 'react-intl';
import { checkAllConditions } from '../../utils/conditionalLogic';
import {
	usePostFormValuesMutation,
	useUpdateFormStatusMutation,
	useUpdateMarkCompleteMutation
} from '../../services/endpoints/formBuilderEndpoints';

import Instructions from '../FormBuilder/customComponents/Instructions';
import { setFormButtonLoadingHasChanged, setFormData } from '../FormBuilderLibrary/slice';
import SimulateSaveButton from '../SimulateSaveButton/SimulateSaveButton';
import { useSaveCoreInfoMutation } from '../../services/endpoints/programsEndpoints';
import { disabledStatuses } from '../../constants/statuses';
import {
	validateRequiredFields,
	checkHiddenFields,
	exportFormToPDF
} from '../../utils/utilFunctions';
import { getLocalAuth } from '../../utils/environmentUtils';
import { calculateUserScore } from '../FormBuilder/utils';
import usePermissions from '../../hooks/usePermissions';
import { sanitizePathname } from '../../utils/utilFunctions';
import {
	saveFormFieldDataBackup,
	clearFormFieldDataBackup,
	getFormFieldDataBackup
} from '../../utils/formBackupUtils';
import AlertBalloon from '../../components/AlertBalloon';

// Local ReactFormGenerator wrapper to avoid circular dependency
function ReactFormGenerator({
	disableFooterButtons,
	formID,
	answer_data,
	onSubmit,
	NextPage,
	GoBack,
	activeStep,
	totalSteps,
	variables,
	formIndex,
	read_only,
	data,
	funded,
	completionErrors,
	setCompletionErrors,
	onValidationError,
	formRef,
	isSimulateSave,
	onBlur,
	onDirty
}) {
	const language = 'en';
	const currentAppLocale = AppLocale[language];
	return (
		<IntlProvider locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
			<FormGenerator
				disableFooterButtons={disableFooterButtons}
				formID={formID}
				answer_data={answer_data}
				onSubmit={onSubmit}
				NextPage={NextPage}
				GoBack={GoBack}
				activeStep={activeStep}
				totalSteps={totalSteps}
				variables={variables}
				formIndex={formIndex}
				read_only={read_only}
				data={data}
				funded={funded}
				completionErrors={completionErrors}
				setCompletionErrors={setCompletionErrors}
				onValidationError={onValidationError}
				formRef={formRef}
				isSimulateSave={isSimulateSave}
				onBlur={onBlur}
				onDirty={onDirty}
			/>
		</IntlProvider>
	);
}

const MultiFormViewer = ({
	formAnswers: formAnswersProps,
	form,
	apiFormData,
	formInstructions,
	readOnly: readOnlyProp,
	disableActionButtons,
	disableFooterButtons,
	hideActionButtons,
	hideInstructions,
	isSimulating,
	isEmbedded,
	status: statusProps,
	application_status,
	formID,
	setReadOnly,
	setReadOnlyTest: setReadOnlyTestProps,
	isDataLoading,
	completionErrors: completionErrorsProps,
	marginBottom,
	newExperience,
	lastEdited,
	funded,
	formRef,
	onCancelClick
}) => {
	const dispatch = useAppDispatch();

	const path = sanitizePathname(window.location.pathname);
	const pathSegments = path.split('/');
	const _ = require('lodash');

	const [alertBalloon, setAlertBalloon] = useState({ isOpen: false });
	const [backupAlert, setBackupAlert] = useState({ isOpen: false, isDismissed: false });
	const [saveErrorAlert, setSaveErrorAlert] = useState({ isOpen: false });
	const [activeStep, setActiveStep] = useState(0);
	const [skipped, setSkipped] = useState(new Set());
	const [formSchema, setFormSchema] = useState();
	const [formAnswers, setFormAnswers] = useState([]);
	const [steps, setSteps] = useState(['']);
	const [readOnlyTest, setReadOnlyTest] = useState(false);
	const [readOnlyReset, setReadOnlyReset] = useState(true);
	const [loadingCancel, setLoadingCancel] = useState(true);
	const [buttonLoading, setButtonLoading] = useState(false);
	const [completionErrors, setCompletionErrors] = useState([]);
	const [isSaving, setIsSaving] = useState(false);
	const [handlingFormSave, setHandlingFormSave] = useState(false);
	// if entry has started, we use answers, otherwise we use answers passed in as a prop
	// this is a current workaround for forms that display answer data, but the data tested
	// against for validation is somehow missing fields
	const [entryStarted, setEntryStarted] = useState(false);
	const [formDirty, setFormDirty] = useState(false);
	const [userHasStartedEditing, setUserHasStartedEditing] = useState(false);

	const appID = pathSegments.find((segment) => segment.includes('api-'))?.replace('api-', '');
	const accountSource = useAppSelector((state) => state?.login?.accountSource);
	const apiToken = getLocalAuth(accountSource)
		?.access_token.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
	const reduxFormSchema = useAppSelector((state) => state?.form?.formSchema);
	// Var from redux store for preview page, form for runtime
	const dependenciesRedux = useAppSelector((state) => state?.form?.dependancies);
	const dependenciesForm = form?.[0]?.filter((e) => 'dependency' in e);
	const allDependancies = form === undefined ? dependenciesRedux : dependenciesForm;
	const answers = useAppSelector((state) => state?.form?.form);
	const formMetaInfo = useAppSelector((state) => state.form.formMetaInfo);
	const saveButtonLoadingState = useAppSelector((state) => state.form.saveButtonLoading);
	const readOnly = readOnlyProp ?? false;
	const { hasEditPermissions, userAccountTypeID } = usePermissions();
	const normalizedApplicationStatus = (application_status ?? '').toString().toLowerCase();
	const isApplicationRejected =
		normalizedApplicationStatus.includes('reject') ||
		normalizedApplicationStatus.includes('denied');
	const matchesDisabledStatus = disabledStatuses.some(
		(status) => status?.toString().toLowerCase() === normalizedApplicationStatus
	);
	const shouldHideFormActions = isApplicationRejected;
	const initialLoadComplete = answers?.length > 0;

	// Check for existing backup immediately when formID is available
	const hasExistingBackup =
		formID &&
		!isSimulating &&
		!isDataLoading &&
		getFormFieldDataBackup(formID)?.fieldData?.length > 0;

	const [updateFormStatus] = useUpdateFormStatusMutation();
	const [postFormValues] = usePostFormValuesMutation();
	const [saveCoreInfo] = useSaveCoreInfoMutation();
	const [updateMarkComplete] = useUpdateMarkCompleteMutation();

	let beforeDeadline = true;
	if (apiFormData) {
		if (apiFormData?.status?.includes('reopened')) {
			beforeDeadline = true;
		} else {
			const endDateRaw =
				apiFormData?.form?.stage?.end_date?.trim() || apiFormData?.form?.stage?.program?.end_date;
			if (endDateRaw) {
				beforeDeadline = new Date(endDateRaw) > new Date();
			} else {
				beforeDeadline = true;
			}
		}
	}

	useEffect(() => {
		if (form) {
			setFormSchema(form);
			setSteps(form?.map(() => ''));
		} else if (reduxFormSchema[0] !== null) {
			let schema = reduxFormSchema?.filter((e) => {
				return e !== undefined;
			});
			setFormSchema(schema);
			setSteps(schema?.map(() => ''));
		}
	}, [form, reduxFormSchema]);

	useEffect(() => {
		setLoadingCancel(false);
		setIsSaving(false);
	}, []);

	useEffect(() => {
		if (buttonLoading) {
			setButtonLoading(saveButtonLoadingState);
		}
	}, [saveButtonLoadingState]);

	useEffect(() => {
		// Prioritize Redux data when user has started editing or when backup has been restored
		if (entryStarted || userHasStartedEditing) {
			setFormAnswers(answers);
			checkAllConditions(allDependancies, answers);
		} else if (formAnswersProps) {
			setFormAnswers(formAnswersProps);
			checkAllConditions(allDependancies, formAnswersProps);
		} else {
			setFormAnswers(answers);
			checkAllConditions(allDependancies, answers);
			if (answers.length > 0) {
				setReadOnlyReset(false);
			}
		}
		if (!isSimulating && isSaving && !handlingFormSave && _.isEqual(formAnswers[1], answers[1])) {
			//If incoming answers are equal to local answers then load form. This doesn't work for when formAnswers and answers exist. Like for a review form. So we will handle that save after the message is displayed
			// This is needed for saving a review form. As with saving an application form we have props.isDataLoading
			handleFormFinishedSaving(true);
			setHandlingFormSave(true);
		}

		if (
			!isSimulating &&
			initialLoadComplete &&
			userHasStartedEditing &&
			answers?.[1]?.length > 0 &&
			formID &&
			!isDataLoading
		) {
			if (formID) {
				saveFormFieldDataBackup(answers[1], formID);
				setBackupAlert({ isOpen: true });
			}
		}
	}, [
		activeStep,
		answers,
		formAnswersProps,
		userHasStartedEditing,
		initialLoadComplete,
		isDataLoading
	]);

	useEffect(() => {
		if (entryStarted || isSimulating) {
			checkAllConditions(allDependancies, answers);
		} else {
			checkAllConditions(allDependancies, formAnswersProps);
		}
	});

	useEffect(() => {
		if (completionErrors.length > 0) {
			setCompletionErrors(validateFormAnswers());
		}
	}, [answers]);

	const handleFormFinishedSaving = (useTimeout = false) => {
		if (useTimeout) {
			setTimeout(() => {
				setButtonLoading(false);
				setLoadingCancel(false);
				setIsSaving(false);
				setHandlingFormSave(false);
				setEntryStarted(false);
				setFormDirty(false);
				setUserHasStartedEditing(false);
			}, 2000);
		} else {
			setButtonLoading(false);
			setLoadingCancel(false);
			setIsSaving(false);
			setHandlingFormSave(false);
			setEntryStarted(false);
			setFormDirty(false);
			setUserHasStartedEditing(false);
		}
	};

	const isStepSkipped = (step) => {
		return skipped.has(step);
	};

	const handleNext = () => {
		let newSkipped = skipped;
		if (isStepSkipped(activeStep)) {
			newSkipped = new Set(newSkipped.values());
			newSkipped.delete(activeStep);
		}

		setActiveStep((prevActiveStep) => prevActiveStep + 1);
		setSkipped(newSkipped);
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleReset = () => {
		setActiveStep(0);
	};

	const handleAlert = (message, status) => {
		if (typeof setReadOnlyTestProps === 'function') {
			// preview
			if (status === 'success') {
				setReadOnlyTestProps(true);
			} else {
				setReadOnlyTestProps(false);
			}
		}
		setButtonLoading(false);
		setAlertBalloon({ isOpen: true, message, status });
		setTimeout(() => {
			setAlertBalloon((prevAlertBalloon) => ({
				...prevAlertBalloon,
				isOpen: false
			}));
		}, 30000);
	};

	useEffect(() => {
		if (!isDataLoading && isSaving) {
			setIsSaving(false);
		}
	}, [isDataLoading]);

	const handleAfterSave = (message, messageType) => {
		setButtonLoading(false);
		setEntryStarted(false);
		setFormDirty(false);
		setUserHasStartedEditing(false);
		setSaveErrorAlert({ isOpen: false });
		handleAlert(message, messageType);
	};

	const handleAfterSaveError = useCallback(
		(message, messageType) => {
			// Restore backup data on save failure so user doesn't lose their work
			let hasBackup = false;
			if (formID) {
				const backup = getFormFieldDataBackup(formID);
				if (backup?.fieldData?.length > 0) {
					dispatch(setFormData([1, backup.fieldData]));
					setUserHasStartedEditing(true);
					setEntryStarted(true);
					hasBackup = true;
				}
			}

			// Show detailed error alert only when there's a backup in localStorage
			if (hasBackup) {
				setSaveErrorAlert({ isOpen: true });
			}

			setButtonLoading(false);
			setIsSaving(false);
			handleAlert(message, messageType);
		},
		[formID, dispatch]
	);

	const handleFormSubmit = async (data) => {
		setIsSaving(true);
		setButtonLoading('save');
		if (isSimulating) {
			handleAlert('Form Saved Successfully!', 'success');
			setReadOnlyTest(true);
			setReadOnly(true);
			handleFormFinishedSaving();
			return;
		}
		const userScore = calculateUserScore(form[0], data);
		checkHiddenFields(form[0], data);

		if (disableFooterButtons && !hideActionButtons) {
			handleAlert('Form Saved Successfully!', 'success');
			setReadOnlyTest(true);
			setReadOnlyReset(true);
			setReadOnly(true);
		} else {
			let noNullAnswers = data.filter((e) => e !== null && e !== undefined);
			let options = {
				formID: formID?.split('-')?.[1],
				payload: {
					noNullAnswers,
					userScore
				},
				apiToken: apiToken,
				application_id: appID
			};

			const formSchema = apiFormData?.form?.form_schema;
			const coreInfoElements = formSchema?.filter(
				(e) =>
					e?.custom_options?.includes('project-title') || e?.custom_options?.includes('org-name')
			);

			// SAVE CORE INFO
			if (apiFormData?.form?.purpose === 'Core Information') {
				const payload = {};
				coreInfoElements?.forEach((e) => {
					const elementAnswer = noNullAnswers?.find((x) => e?.field_name === x?.name);
					if (e?.custom_options?.includes('org-name')) {
						payload['org_name'] = elementAnswer?.value || '';
					}
					if (e?.custom_options?.includes('project-title')) {
						payload['project_title'] = elementAnswer?.value || '';
					}
				});

				try {
					await saveCoreInfo({
						...options,
						payload: payload
					}).unwrap();

					try {
						await postFormValues(options).unwrap();
						const formIdForBackup = formID; // Use form ID as-is, preserving the hyphen
						if (formIdForBackup) {
							clearFormFieldDataBackup(formIdForBackup);
							// Hide backup alert when backup is cleared after successful save
							setBackupAlert({ isOpen: false, isDismissed: false });
						}
						handleAfterSave('Form Saved Successfully!', 'success');
						setReadOnly && setReadOnly(true);
					} catch (error) {
						handleAfterSaveError('Form Saving Failed!', 'error');
					}
				} catch (error) {
					handleAfterSaveError('Form Saving Failed!', 'error');
				}
			} else {
				postFormValues(options)
					.unwrap()
					.then(() => {
						if (formID) {
							clearFormFieldDataBackup(formID);
							setBackupAlert({ isOpen: false, isDismissed: false });
						}

						handleAfterSave('Form Saved Successfully!', 'success');
						setReadOnly && setReadOnly(true);
					})
					.catch(() => handleAfterSaveError('Form Saving Failed!', 'error'));
			}
		}
	};

	const handleCancel = () => {
		const proceedWithCancel = () => {
			setLoadingCancel(true);
			setUserHasStartedEditing(false);
			dispatch(setFormData([1, apiFormData?.field_values] || []));
			setFormAnswers([null, apiFormData?.field_values] || []);
			setReadOnly(true);
			setCompletionErrors([]);
			setTimeout(function () {
				setLoadingCancel(false);
			}, 1000);
			setEntryStarted(false);
			setFormDirty(false);

			if (formID) {
				clearFormFieldDataBackup(formID);
				setBackupAlert({ isOpen: false, isDismissed: false });
			}
		};

		if (onCancelClick) {
			onCancelClick(proceedWithCancel);
		} else {
			proceedWithCancel();
		}
	};

	const handleReopenForm = () => {
		setButtonLoading('reopen');

		let id = formID?.split('-')?.[1];
		const options = {
			id: id,
			status: 'Draft (returned)',
			apiToken: apiToken
		};
		const completeOptions = {
			id: id,
			complete: false,
			apiToken: apiToken
		};

		updateMarkComplete(completeOptions)
			.unwrap()
			.then(() => {
				updateFormStatus(options)
					.unwrap()
					.then(() => {
						handleAlert('Status Updated Successfully!', 'success');
					})
					.catch(() => {
						handleAlert('Status Update Failed!', 'error');
					});
			})
			.catch(() => {
				handleAlert('Status Update Failed!', 'error');
			});
	};

	const handleValidationError = () => {
		if (newExperience) {
			setButtonLoading(false);
			setIsSaving(false);
		}
	};

	const handleCompleteForm = () => {
		if (!hasEditPermissions) {
			handleAlert('You do not have permission to complete this form.', 'error');
			return;
		}
		const completeValidationErrors = validateRequiredFields(form[0], formAnswersProps[1]);
		const validForm = completeValidationErrors.length === 0;

		if (validForm) {
			setButtonLoading('complete');
			let id = formID?.split('-')?.[1];
			const options = {
				id,
				status: 'Completed',
				apiToken
			};
			const completeOptions = {
				id: formID?.split('-')?.[1],
				complete: true,
				apiToken: apiToken
			};
			updateMarkComplete(completeOptions)
				.unwrap()
				.then(() => {
					updateFormStatus(options)
						.unwrap()
						.then(() => {
							handleAlert('Status Updated Successfully!', 'success');
						})
						.catch(() => {
							handleAlert('Status Update Failed!', 'error');
						});
				})
				.catch(() => {
					handleAlert('Status Update Failed!', 'error');
				});
		} else {
			setCompletionErrors(completeValidationErrors);
		}
	};

	const handleSave = async () => {
		if (!newExperience) setButtonLoading('save');
		dispatch(setFormButtonLoadingHasChanged('save'));
		const element = document.getElementById(`${formID}-saveButton`);
		element.click();
	};

	useEffect(() => {
		if (setReadOnly) {
			setReadOnly(readOnlyProp);
		}
	}, [readOnlyProp]);

	const validateFormAnswers = () => {
		if (entryStarted) {
			return validateRequiredFields(form[0], answers[1]);
		} else {
			return validateRequiredFields(form[0], formAnswersProps[1]);
		}
	};

	const handleFormBlur = () => {
		// After the first blur we want to start using the values in the answer store
		// this is because those values are updated and also because on load the
		// values in props are more accurate.
		if (!entryStarted) setEntryStarted(true);

		if (!userHasStartedEditing) setUserHasStartedEditing(true);

		if (saveErrorAlert.isOpen) {
			setSaveErrorAlert({ isOpen: false });
		}
	};

	const handleFormDirty = () => {
		if (!formDirty) setFormDirty(true);

		if (!userHasStartedEditing) setUserHasStartedEditing(true);

		if (saveErrorAlert.isOpen) {
			setSaveErrorAlert({ isOpen: false });
		}
	};

	const setComplete = (isComplete) => {
		const completeOptions = {
			id: formID?.split('-')?.[1],
			complete: isComplete,
			apiToken: apiToken
		};
		return updateMarkComplete(completeOptions);
	};

	const formHasRequiredFields = () => {
		return form[0].some((field) => field.required ?? false);
	};

	const formHasNoAnswers = () => {
		// Core Info has an automatic field that's also treated as an answer but
		// shouldn't for purposes of validation
		const noAnswersThreshold = apiFormData?.form?.purpose === 'Core Information' ? 1 : 0;

		return (
			formAnswersProps[1].length <= noAnswersThreshold && answers[1].length <= noAnswersThreshold
		);
	};

	const handleSaveMode = (mode) => {
		if (mode === 'complete') {
			if (formHasRequiredFields() && formHasNoAnswers()) {
				handleAlert('Please populate the form before marking it complete.', 'warning');
				return;
			}

			const errors = validateFormAnswers();

			if (errors.length > 0) {
				setCompletionErrors(errors);
				return;
			}
		}

		setButtonLoading(mode);
		setComplete(mode === 'complete').then(() => {
			handleSave();
		});
	};

	const hasLastEdited = (lastEdited) => {
		return lastEdited?.by && lastEdited?.on;
	};

	// Prioritize Redux data when user has started editing or when entry has started
	// This ensures backup restoration works immediately after save failures
	const isEntryOrEditStarted = entryStarted || userHasStartedEditing;
	let nextStepAnswers;
	if (isEntryOrEditStarted) {
		nextStepAnswers = formAnswers[activeStep + 1];
	} else if (formAnswersProps) {
		nextStepAnswers = formAnswersProps[activeStep + 1];
	} else {
		nextStepAnswers = formAnswers[activeStep + 1];
	}

	const shouldShowBackupAlert =
		(backupAlert.isOpen || hasExistingBackup) && !saveErrorAlert.isOpen && !backupAlert.isDismissed;

	return (
		<Box sx={{ width: '100%' }}>
			<Helmet>
				<link rel="stylesheet" href="https://use.fontawesome.com/releases/v5.13.0/css/all.css" />
				<link
					rel="stylesheet"
					href="https://stackpath.bootstrapcdn.com/bootstrap/4.4.1/css/bootstrap.min.css"
					integrity="sha384-Vkoo8x4CGsO3+Hhxv8T/Q5PaXtkKtu6ug5TOeNV6gBiFeWPGFN9MuhOf23Q9Ifjh"
					crossOrigin="anonymous"
				/>
			</Helmet>
			<Stepper activeStep={activeStep}>
				{steps.map((label) =>
					steps?.length > 1 ? (
						<Step key={label}>
							<StepLabel></StepLabel>
						</Step>
					) : null
				)}
			</Stepper>

			<Collapse in={alertBalloon.isOpen}>
				<StyledWrapper.StyledAlert
					severity={alertBalloon?.status}
					action={
						<IconButton
							aria-label="close"
							color={alertBalloon?.status}
							size="small"
							onClick={() => setAlertBalloon({ isOpen: false })}
						>
							{alertBalloon?.status !== 'success' ? <CloseIcon fontSize="inherit" /> : ''}
						</IconButton>
					}
					sx={{ mb: 2, mt: 2 }}
				>
					{alertBalloon?.message}
				</StyledWrapper.StyledAlert>
			</Collapse>

			<AlertBalloon
				open={shouldShowBackupAlert}
				message="This form contains data that has not yet been saved to the server. Click the Save button to commit your edits."
				onClose={() => setBackupAlert({ isOpen: false, isDismissed: true })}
				severity="info"
				variant="outlined"
			/>

			<AlertBalloon
				open={saveErrorAlert.isOpen}
				message={[
					'Your application has not been saved due to a network or application issue. Please try saving again. If the issue persists, verify that your device is connected to the network and that your Application and Review Manager session has not expired.',
					'If your session has timed out, open a new session and log into Grants Network / Application and Review Manager again. You may then try submitting this tab again.'
				]}
				onClose={() => setSaveErrorAlert({ isOpen: false })}
				severity="error"
				variant="outlined"
			/>

			{activeStep === steps.length ? (
				<>
					<Typography sx={{ mt: 2, mb: 1 }}>All steps completed - you&apos;re finished</Typography>
					<Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
						<Box sx={{ flex: '1 1 auto' }} />
						<Button onClick={handleReset}>Reset</Button>
					</Box>
				</>
			) : (
				<>
					{disableFooterButtons && !hideActionButtons && (
						<SimulateSaveButton
							setReadOnly={setReadOnly}
							marginBottom={marginBottom}
							setOuterLoadingCancel={setLoadingCancel}
							readOnlyTest={readOnlyTest}
							readOnlyReset={readOnlyReset}
							setReadOnlyReset={setReadOnlyReset}
							setReadOnlyTest={setReadOnlyTest}
							setAlertBalloon={setAlertBalloon}
							formMetaInfo={formMetaInfo}
							applicationId={appID}
						/>
					)}

					{!beforeDeadline && apiFormData?.status != 'Completed' && (
						<Alert variant="outlined" severity="error">
							The deadline has passed for this program. This application can no longer be submitted.
						</Alert>
					)}

					{(formMetaInfo?.instructions || formInstructions) && !hideInstructions && (
						<Instructions data={{ content: formInstructions || formMetaInfo?.instructions }} />
					)}

					{newExperience && (
						<StyledWrapper.EditBar>
							{apiFormData?.status !== 'Not Started' && hasLastEdited(lastEdited) && (
								<StyledWrapper.LastEdited>
									{`Last edited by: ${lastEdited.by}, ${lastEdited.on}`}
								</StyledWrapper.LastEdited>
							)}

							<StyledWrapper.LastEditedActions>
								{!isEmbedded && formID && apiFormData?.status !== 'Not Started' && readOnlyProp && (
									<Button
										variant="contained"
										onClick={() => exportFormToPDF(apiFormData, application_status, appID, false)}
										sx={{ height: '32px', marginTop: '8px' }}
									>
										Export to PDF
									</Button>
								)}
								{hasEditPermissions &&
									statusProps?.toLowerCase()?.includes('completed') &&
									!matchesDisabledStatus &&
									!shouldHideFormActions && (
										<LoadingButton
											loading={buttonLoading === 'reopen'}
											variant="contained"
											component="label"
											data-testid={'individualapp-reopen-button'}
											onClick={handleReopenForm}
											sx={{ height: '32px', marginTop: '8px' }}
										>
											Reopen
										</LoadingButton>
									)}
								{!disableActionButtons &&
									(hasEditPermissions || !userAccountTypeID) &&
									(readOnlyProp ? (
										!shouldHideFormActions && (
											<>
												<Button
													disabled={apiFormData?.status === 'Completed'}
													data-testid={'individualapp-edit-button'}
													variant="outlined"
													onClick={() => {
														setReadOnly(false);
													}}
													startIcon={<EditIcon />}
													sx={{ height: '32px', marginTop: '8px' }}
												>
													Edit
												</Button>
												{hasEditPermissions &&
													beforeDeadline &&
													statusProps?.toLowerCase()?.includes('draft') && (
														<LoadingButton
															loading={buttonLoading === 'complete'}
															variant="contained"
															component="label"
															data-testid={'individualapp-save-button'}
															onClick={handleCompleteForm}
														>
															Complete
														</LoadingButton>
													)}
											</>
										)
									) : (
										<>
											{!isEmbedded && formID && apiFormData?.status !== 'Not Started' && (
												<Button
													disabled={buttonLoading}
													variant="contained"
													onClick={() =>
														exportFormToPDF(apiFormData, application_status, appID, false)
													}
												>
													Export to PDF
												</Button>
											)}
											<LoadingButton
												loading={buttonLoading === 'draft'}
												disabled={buttonLoading || (!formDirty && !hasExistingBackup)}
												variant="contained"
												component="label"
												data-testid={'individualapp-save-button'}
												onClick={() => handleSaveMode('draft')}
											>
												Save As Draft
											</LoadingButton>
											<LoadingButton
												loading={buttonLoading === 'complete'}
												disabled={buttonLoading}
												variant="contained"
												component="label"
												data-testid={'individualapp-complete-button'}
												onClick={() => handleSaveMode('complete')}
											>
												Save As Complete
											</LoadingButton>
											<Button
												disabled={buttonLoading}
												variant="outlined"
												onClick={handleCancel}
												data-testid={'individualapp-cancel-button'}
											>
												Cancel
											</Button>
										</>
									))}
							</StyledWrapper.LastEditedActions>
						</StyledWrapper.EditBar>
					)}

					{!newExperience && (
						<StyledWrapper.EditBar>
							{apiFormData?.status !== 'Not Started' && hasLastEdited(lastEdited) && (
								<StyledWrapper.LastEdited>
									{`Last edited by: ${lastEdited.by}, ${lastEdited.on}`}
								</StyledWrapper.LastEdited>
							)}

							<StyledWrapper.LastEditedActions>
								{!isEmbedded && formID && apiFormData?.status !== 'Not Started' && (
									<Button
										variant="contained"
										onClick={() => exportFormToPDF(apiFormData, application_status, appID, false)}
									>
										Export to PDF
									</Button>
								)}
								{hasEditPermissions &&
									statusProps?.toLowerCase()?.includes('completed') &&
									!matchesDisabledStatus &&
									!shouldHideFormActions && (
										<LoadingButton
											loading={buttonLoading === 'reopen'}
											variant="contained"
											component="label"
											data-testid={'individualapp-reopen-button'}
											onClick={handleReopenForm}
										>
											Reopen
										</LoadingButton>
									)}
								{!disableActionButtons &&
									(hasEditPermissions || !userAccountTypeID) &&
									(readOnlyProp ? (
										!shouldHideFormActions && (
											<>
												<Button
													disabled={apiFormData?.status === 'Completed'}
													data-testid={'individualapp-edit-button'}
													variant="outlined"
													onClick={() => {
														setReadOnly(false);
													}}
													startIcon={<EditIcon />}
												>
													{formID?.includes('afi') ? 'Edit Application Form' : 'Edit'}
												</Button>

												{hasEditPermissions &&
													beforeDeadline &&
													statusProps?.toLowerCase()?.includes('draft') && (
														<LoadingButton
															loading={buttonLoading === 'complete'}
															variant="contained"
															component="label"
															data-testid={'individualapp-save-button'}
															onClick={handleCompleteForm}
														>
															Complete
														</LoadingButton>
													)}
											</>
										)
									) : (
										<>
											<LoadingButton
												loading={buttonLoading === 'save'}
												variant="contained"
												component="label"
												data-testid={'individualapp-save-button'}
												onClick={handleSave}
											>
												Save
											</LoadingButton>
											<Button
												variant="outlined"
												onClick={handleCancel}
												data-testid={'individualapp-cancel-button'}
											>
												Cancel
											</Button>
										</>
									))}
							</StyledWrapper.LastEditedActions>
						</StyledWrapper.EditBar>
					)}

					{loadingCancel || isSaving ? (
						<Box
							sx={{
								display: 'flex',
								width: '100%',
								height: '100%',
								alignItems: 'center',
								justifyContent: 'center'
							}}
						>
							<CircularProgress />
						</Box>
					) : (
						<ReactFormGenerator
							disableFooterButtons={disableFooterButtons}
							formID={formID || 'testing-form'}
							answer_data={nextStepAnswers}
							onSubmit={handleFormSubmit}
							NextPage={() => handleNext()}
							GoBack={() => handleBack()}
							activeStep={activeStep}
							totalSteps={steps}
							variables={[]}
							formIndex={activeStep + 1}
							read_only={isSimulating === true ? readOnlyTest : readOnly}
							data={formSchema ? formSchema[activeStep] : []}
							funded={funded}
							completionErrors={
								completionErrors?.length === 0 ? completionErrorsProps : completionErrors
							}
							setCompletionErrors={setCompletionErrors}
							onValidationError={handleValidationError}
							formRef={formRef}
							isSimulateSave={isSimulating}
							onBlur={handleFormBlur}
							onDirty={handleFormDirty}
						/>
					)}
				</>
			)}
		</Box>
	);
};

export default MultiFormViewer;
